import React, { useState, useEffect, useRef, useCallback } from "react";
import LogRocket from "logrocket";
import axios from "axios";
import "./App.css";
import ScreenshotViewer from "./components/ScreenshotViewer";
import ProjectProblemWorkspace from "./components/ProjectProblemWorkspace";
import Login from "./components/Login";
import ProjectList from "./components/ProjectList";
import DisputeResponseModal from "./components/DisputeResponseModal";
import {
  fetchUserProjects,
  fetchProject,
  fetchProjectProblemRuns,
  fetchFirstUnlabeledRun,
  ProjectWithStatistics,
  ProjectResponse,
  ProblemRunWithProblem,
  invalidateAPICache,
  completeProject,
  fetchIncorrectLabels,
  fetchProblemRunById,
} from "./api";
import { ScoreDispute, HumanLabel } from "./types";
import { TutorialProvider, useTutorial } from "./services/tutorial";
import {
  getAllTutorialConfigs,
  getTutorialForUser,
  getTutorialConfig,
} from "./services/tutorial/registry";
import { preloadService } from "./services/PreloadService";
import { extractPromptFromMessages } from "./utils/promptExtractor";
import { preloadFFprobe } from "./utils/ffmpegLoader";

// Use full URL in development (when running npm start), relative URL in production (Docker with nginx)
const API_BASE_URL = process.env.NODE_ENV === 'development' ? "https://api.habitat.inc/v1" : "/api/v1";
const getAuthHeaders = () => {
  const accessCode = localStorage.getItem("accessCode");
  return accessCode ? { Authorization: `Bearer ${accessCode}` } : {};
};

const SOLUTION_UPLOAD_NOTE = "SOLUTION_UPLOAD";
const PROJECT_TUTORIAL_SOFTWARES = new Set(["audacity", "shotcut", "gimp", "libreoffice", "inkscape"]);

// Main App component content that uses the tutorial context
interface AppContentProps {
  onAuthStateChange: (userId: string | null, permissions: string[]) => void;
}

function AppContent({ onAuthStateChange }: AppContentProps) {
  const tutorial = useTutorial();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [projects, setProjects] = useState<ProjectWithStatistics[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [selectedProjectData, setSelectedProjectData] =
    useState<ProjectWithStatistics | null>(null);
  const [projectRuns, setProjectRuns] = useState<ProblemRunWithProblem[]>([]);
  const [projectIsReadonly, setProjectIsReadonly] = useState(false);
  const [tutorialProjectLoadedId, setTutorialProjectLoadedId] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<ProblemRunWithProblem | null>(
    null,
  );
  const [totalProjectRuns, setTotalProjectRuns] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [loadingFromUrl, setLoadingFromUrl] = useState(false);
  const [viewingMistake, setViewingMistake] = useState(false);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const hasNotifiedParentRef = useRef(false);
  const [showDisputeResponseModal, setShowDisputeResponseModal] =
    useState(false);
  const [selectedDispute, setSelectedDispute] = useState<ScoreDispute | null>(
    null,
  );
  const [selectedProblemId, setSelectedProblemId] = useState<string | null>(null);
  const selectRunByIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= projectRuns.length) return;
      const run = projectRuns[index];
      setSelectedRun(run);
      const problemId = run.problem?.id || run.problem_id || null;
      setSelectedProblemId(problemId);

      const pathProjectMatch = window.location.pathname.match(
        /\/project\/([^\/]+)/,
      );
      const projectIdFromUrl = pathProjectMatch
        ? pathProjectMatch[1]
        : selectedProject;

      if (projectIdFromUrl && problemId) {
        // Filter runs to only those for this problem to get local index
        const runsForProblem = projectRuns.filter(
          (r) => (r.problem?.id || r.problem_id) === problemId
        );
        const localIndex = runsForProblem.findIndex((r) => r.id === run.id);

        window.history.pushState(
          {},
          "",
          `/project/${projectIdFromUrl}/problems/${problemId}/runs/${localIndex}`,
        );
      }
    },
    [projectRuns, selectedProject],
  );

  const handleRunSelect = useCallback(
    (runId: string) => {
      const tutorialActiveForProject =
        tutorial.state.isActive &&
        PROJECT_TUTORIAL_SOFTWARES.has(tutorial.state.software) &&
        tutorial.state.currentTaskIndex > 0;

      console.debug('[App] handleRunSelect invoked', {
        runId,
        tutorialActive: tutorial.state.isActive,
        tutorialSoftware: tutorial.state.software,
        tutorialStep: tutorial.state.currentStepId,
        tutorialActiveForProject,
      });

      if (tutorialActiveForProject) {
        console.debug('[App] Ignoring manual run select during project tutorial', {
          runId,
          software: tutorial.state.software,
        });
        return;
      }

      const runIndex = projectRuns.findIndex((run) => run.id === runId);
      if (runIndex >= 0) {
        console.debug('[App] Manual run select resolved to index', {
          runId,
          runIndex,
          runProblemId: projectRuns[runIndex]?.problem?.id,
        });
        selectRunByIndex(runIndex);
      } else {
        console.warn('[App] Manual run select did not match any run', { runId });
      }
    },
    [
      projectRuns,
      tutorial.state.currentStepId,
      tutorial.state.isActive,
      tutorial.state.software,
      selectRunByIndex,
    ],
  );

  const handleProblemSelect = useCallback(
    (problemId: string) => {
      const tutorialActiveForProject =
        tutorial.state.isActive &&
        PROJECT_TUTORIAL_SOFTWARES.has(tutorial.state.software) &&
        tutorial.state.currentTaskIndex > 0;

      console.debug('[App] handleProblemSelect invoked', {
        problemId,
        tutorialActive: tutorial.state.isActive,
        tutorialSoftware: tutorial.state.software,
        tutorialStep: tutorial.state.currentStepId,
        tutorialActiveForProject,
      });

      if (tutorialActiveForProject) {
        console.debug('[App] Ignoring manual problem select during project tutorial', {
          problemId,
          software: tutorial.state.software,
        });
        return;
      }

      setSelectedProblemId(problemId);
      setSelectedRun(null);

      if (selectedProject) {
        window.history.pushState({}, "", `/project/${selectedProject}/problems/${problemId}`);
      }
    },
    [
      selectedProject,
      tutorial.state.currentStepId,
      tutorial.state.isActive,
      tutorial.state.software,
    ],
  );

  const handleExitRunView = useCallback(() => {
    // Capture problem ID from the current run before clearing it
    const problemIdForUrl = selectedRun?.problem?.id || selectedRun?.problem_id || selectedProblemId;

    console.log('handleExitRunView debug:', {
      selectedRun: selectedRun,
      problemFromRun: selectedRun?.problem?.id,
      problemIdProp: selectedRun?.problem_id,
      selectedProblemId: selectedProblemId,
      problemIdForUrl: problemIdForUrl,
      selectedProject: selectedProject
    });

    if (problemIdForUrl) {
      setSelectedProblemId(problemIdForUrl);
    }

    setSelectedRun(null);
    if (selectedProject && problemIdForUrl) {
      console.log('Navigating to:', `/project/${selectedProject}/problems/${problemIdForUrl}`);
      window.history.pushState({}, "", `/project/${selectedProject}/problems/${problemIdForUrl}`);
    } else if (selectedProject) {
      console.log('Navigating to (no problem):', `/project/${selectedProject}`);
      window.history.pushState({}, "", `/project/${selectedProject}`);
    } else {
      console.log('Navigating to home');
      window.history.pushState({}, "", "/");
    }
  }, [selectedProject, selectedProblemId, selectedRun]);

  const handleSolutionUploaded = useCallback(
    async (problemId: string) => {
      if (!selectedProject) {
        return;
      }

      try {
        const runsPerPage = 100;
        let page = 0;
        let hasMore = true;
        let totalRuns = totalProjectRuns;
        const refreshedRuns: ProblemRunWithProblem[] = [];

        let isReadonly = projectIsReadonly;

        while (hasMore) {
          const { items, metadata } = await fetchProjectProblemRuns(
            selectedProject,
            page,
            runsPerPage,
          );
          refreshedRuns.push(...items);
          hasMore = Boolean(metadata?.has_next);
          totalRuns = metadata.total || totalRuns;
          if (metadata.tutorial_readonly !== undefined) {
            isReadonly = Boolean(metadata.tutorial_readonly);
          }
          page += 1;

          if (!hasMore) {
            break;
          }

          if (page > 50) {
            console.warn(
              "Aborting project run refresh early to prevent excessive requests",
            );
            break;
          }
        }

        if (refreshedRuns.length) {
          setProjectRuns(refreshedRuns);
        }

        setProjectIsReadonly(Boolean(isReadonly));

        if (totalRuns) {
          setTotalProjectRuns(totalRuns);
        }

        if (selectedRun) {
          const updatedRun = refreshedRuns.find((run) => run.id === selectedRun.id);
          if (!updatedRun) {
            setSelectedRun(null);
          }
        }

        setSelectedProblemId((current) => current || problemId);
      } catch (error) {
        console.error("Error refreshing runs after solution upload:", error);
      }
    },
    [projectIsReadonly, selectedProject, selectedRun, totalProjectRuns],
  );

  const handleRunLabelChange = useCallback(
    (runId: string, label: HumanLabel | null) => {
      if (!userId) {
        return;
      }

      const computeUpdatedLabels = (existing?: HumanLabel[]) => {
        const filtered = (existing || []).filter(
          (existingLabel) =>
            existingLabel.user_id !== userId ||
            existingLabel.notes === SOLUTION_UPLOAD_NOTE,
        );

        return label ? [...filtered, label] : filtered;
      };

      setProjectRuns((prev) =>
        prev.map((run) => {
          if (run.id !== runId) {
            return run;
          }

          return {
            ...run,
            human_labels: computeUpdatedLabels(run.human_labels),
          };
        }),
      );

      setSelectedRun((prev) => {
        if (!prev || prev.id !== runId) {
          return prev;
        }

        return {
          ...prev,
          human_labels: computeUpdatedLabels(prev.human_labels),
        };
      });
    },
    [userId],
  );

  const handleBadTaskReasonUpdate = useCallback(
    (problemId: string, reason: string) => {
      const normalizedReason = reason.trim() ? reason : null;
      setProjectRuns((prev) =>
        prev.map((run) => {
          const runProblemId = run.problem?.id || run.problem_id;
          if (runProblemId !== problemId) {
            return run;
          }
          return {
            ...run,
            label_platform_bad_task_reason: normalizedReason,
          };
        }),
      );

      setSelectedRun((prev) => {
        if (!prev) {
          return prev;
        }
        const runProblemId = prev.problem?.id || prev.problem_id;
        if (runProblemId !== problemId) {
          return prev;
        }
        return {
          ...prev,
          label_platform_bad_task_reason: normalizedReason,
        };
      });
    },
    [],
  );



  // Derive tutorial state from context
  const showOnboarding = tutorial.state.isActive;
  const currentOnboardingTask = tutorial.state.currentTaskIndex;
  const tutorialProblem = tutorial.getCurrentTaskData();
  const tutorialActive = tutorial.state.isActive;
  const usesProjectTutorialLayout = PROJECT_TUTORIAL_SOFTWARES.has(
    tutorial.state.software,
  );

  const handleTutorialButtonClick = useCallback(() => {
    if (tutorialActive) {
      console.debug('[App] Exiting tutorial');
      tutorial.exitTutorial();
      return;
    }

    const tutorialConfig = getTutorialForUser(userPermissions);
    console.debug('[App] Show Tutorial clicked', {
      userPermissions,
      tutorialConfig,
    });
    if (!tutorialConfig) {
      alert('No tutorial available for your current permissions');
      return;
    }
    console.debug('[App] Starting tutorial', {
      software: tutorialConfig.software,
    });
    tutorial.startTutorial(tutorialConfig.software);
  }, [tutorialActive, tutorial, userPermissions]);

  const tutorialButtonLabel = tutorialActive ? 'Exit Tutorial' : 'Show Tutorial';

  // Check URL immediately to prevent flash of project list
  const [initialUrlProject, setInitialUrlProject] = useState<string | null>(
    () => {
      const path = window.location.pathname;
      // Match project URLs with or without problem ID
      const projectMatch = path.match(/^\/project\/([\w-]+)(?:\/problems\/([\w-]+))?$/);
      return projectMatch ? projectMatch[1] : null;
    },
  );

  const [initialUrlProblem, setInitialUrlProblem] = useState<string | null>(
    () => {
      const path = window.location.pathname;
      const problemMatch = path.match(/^\/project\/[\w-]+\/problems\/([\w-]+)$/);
      return problemMatch ? problemMatch[1] : null;
    },
  );

  const [initialUrlMistake, setInitialUrlMistake] = useState<string | null>(
    () => {
      const path = window.location.pathname;
      const mistakeMatch = path.match(/\/mistake\/([^\/]+)/);
      return mistakeMatch ? mistakeMatch[1] : null;
    },
  );

  // Check for direct run URL (for dispute viewing)
  const [initialUrlRun, setInitialUrlRun] = useState<string | null>(() => {
    const path = window.location.pathname;
    const runMatch = path.match(/\/run\/([^\/]+)/);
    return runMatch ? runMatch[1] : null;
  });

  // Check if we're in dispute mode
  const [isDisputeMode, setIsDisputeMode] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("mode") === "dispute";
  });

  const [activeTab, setActiveTab] = useState<
    | "my-projects"
    | "new-projects"
    | "my-mistakes"
    | "my-disputes"
    | "disputes"
    | "resolved-disputes"
    | "conflicting-disputes"
    | "playground"
  >(() => {
    const path = window.location.pathname;
    if (path === "/new-projects") return "new-projects";
    if (path === "/mistakes") return "my-mistakes";
    if (path === "/disputes") return "my-disputes";
    if (path === "/all-disputes") return "disputes";
    if (path === "/resolved-disputes") return "resolved-disputes";
    if (path === "/conflicting-disputes") return "conflicting-disputes";
    if (path === "/playground") return "playground";
    return "my-projects";
  });

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === "/new-projects") {
        setActiveTab("new-projects");
      } else if (path === "/mistakes") {
        setActiveTab("my-mistakes");
      } else if (path === "/disputes") {
        setActiveTab("my-disputes");
      } else if (path === "/all-disputes") {
        setActiveTab("disputes");
      } else if (path === "/resolved-disputes") {
        setActiveTab("resolved-disputes");
      } else if (path === "/conflicting-disputes") {
        setActiveTab("conflicting-disputes");
      } else if (path === "/playground") {
        setActiveTab("playground");
      } else if (path === "/" || path === "/projects") {
        setActiveTab("my-projects");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const storedUserId = localStorage.getItem("userId");
    const storedUsername = localStorage.getItem("username");
    const storedAccessCode = localStorage.getItem("accessCode");

    if (storedUserId && storedUsername && storedAccessCode) {
      setUserId(storedUserId);
      setUsername(storedUsername);
      setIsAuthenticated(true);

      // Restore permissions from localStorage
      const storedPermissions = localStorage.getItem("userPermissions");
      if (storedPermissions) {
        const permissions = JSON.parse(storedPermissions);
        setUserPermissions(permissions);
        // Notify parent of restored auth state - but only do this once!
        if (!hasNotifiedParentRef.current) {
          hasNotifiedParentRef.current = true;
          // Use setTimeout to ensure it runs after initial render
          setTimeout(() => {
            onAuthStateChange(storedUserId, permissions);
          }, 0);
        }
      } else {
        // If we have an access code but no permissions, fetch them from the API
        axios
          .get(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${storedAccessCode}` },
          })
          .then((response) => {
            const permissions = response.data.permissions || [];
            localStorage.setItem(
              "userPermissions",
              JSON.stringify(permissions),
            );
            setUserPermissions(permissions);
            onAuthStateChange(storedUserId, permissions);
          })
          .catch((error) => {
            console.error("Failed to fetch permissions:", error);
            // Even on error, notify parent with empty array
            onAuthStateChange(storedUserId, []);
          });
      }

      // Identify user in LogRocket for existing sessions
      LogRocket.identify(storedUserId, {
        name: storedUsername,
        // Add any other user properties you want to track
      });

      // Check onboarding status
      checkOnboardingStatus(storedUserId);

      // Check if we have a project/run, mistake, or direct run in the URL
      const path = window.location.pathname;
      const projectProblemRunMatch = path.match(/^\/project\/([\w-]+)\/problems\/([\w-]+)\/runs\/(\d+)$/);
      const projectWithProblemMatch = path.match(/^\/project\/([\w-]+)\/problems\/([\w-]+)$/);
      const projectOnlyMatch = path.match(/^\/project\/([\w-]+)$/);
      const mistakeMatch = path.match(/\/mistake\/([^\/]+)/);
      const runMatch = path.match(/\/run\/([^\/]+)/);

      if (projectProblemRunMatch) {
        const [, projectId, problemId, localRunIndex] = projectProblemRunMatch;
        setLoadingFromUrl(true);
        setSelectedProject(projectId); // Set immediately to prevent showing project list
        loadProjectProblemAndRun(projectId, problemId, parseInt(localRunIndex));
      } else if (projectWithProblemMatch) {
        // Handle /project/{project_id}/problems/{problem_id}
        const [, projectId, problemId] = projectWithProblemMatch;
        setLoadingFromUrl(true);
        setSelectedProject(projectId); // Set immediately to prevent showing project list
        setInitialUrlProject(projectId);
        setInitialUrlProblem(problemId);
      } else if (projectOnlyMatch) {
        // Handle /project/{project_id}
        const [, projectId] = projectOnlyMatch;
        setLoadingFromUrl(true);
        setSelectedProject(projectId); // Set immediately to prevent showing project list
        setInitialUrlProject(projectId);
      } else if (mistakeMatch) {
        const [, runId] = mistakeMatch;
        setLoadingFromUrl(true);
        setViewingMistake(true);
        loadMistakeFromUrl(runId);
      } else if (runMatch) {
        const [, runId] = runMatch;
        setLoadingFromUrl(true);
        setInitialUrlRun(runId);
      }
    } else {
      // Clear any partial auth data
      localStorage.removeItem("userId");
      localStorage.removeItem("username");
      localStorage.removeItem("accessCode");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAuthStateChange]);

  // Preload FFprobe in the background for video metadata extraction
  useEffect(() => {
    // Wait a bit after page load to not interfere with initial rendering
    const timer = setTimeout(() => {
      console.log("[App] Starting FFprobe background preload");
      preloadFFprobe();
    }, 3000); // Wait 3 seconds after mount

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (
      isAuthenticated &&
      userId &&
      hasCompletedOnboarding &&
      !selectedProject &&
      !loadingFromUrl
    ) {
      loadProjects();
    }
  }, [
    isAuthenticated,
    userId,
    hasCompletedOnboarding,
    selectedProject,
    loadingFromUrl,
  ]);

  // Load projects in the background even during onboarding
  useEffect(() => {
    if (isAuthenticated && userId && projects.length === 0 && !loadingFromUrl) {
      loadProjects();
    }
  }, [isAuthenticated, userId, loadingFromUrl]);


  // Handle screenshots loaded - trigger prefetch of next run
  const handleScreenshotsLoaded = useCallback(() => {
    if (!selectedRun || !projectRuns.length || viewingMistake || projectIsReadonly) return;

    const currentIndex = projectRuns.findIndex((r) => r.id === selectedRun.id);

    // Preload next run if available
    if (currentIndex >= 0 && currentIndex < projectRuns.length - 1) {
      const nextRun = projectRuns[currentIndex + 1];
      if (nextRun && nextRun.problem) {
        console.log('[App] Current run screenshots loaded, starting preload of next run:', nextRun.id);
        preloadService.preload(nextRun.problem.name, nextRun.id);
      }
    }
  }, [selectedRun, projectRuns, viewingMistake, projectIsReadonly]);

  const loadRunFromUrl = useCallback(
    async (runId: string) => {
      console.log('loadRunFromUrl called with runId:', runId);
      
      // Check URL params directly to ensure we have the latest mode
      const params = new URLSearchParams(window.location.search);
      const urlIsDisputeMode = params.get("mode") === "dispute";
      const disputeId = params.get("disputeId");
      
      console.log('URL params:', { urlIsDisputeMode, disputeId });

      // Update state if needed
      if (urlIsDisputeMode !== isDisputeMode) {
        setIsDisputeMode(urlIsDisputeMode);
      }

      console.log('Setting loading to true');
      setLoading(true);

      try {
        console.log('Inside try block, about to wait 100ms');
        // Small delay to ensure auth headers are available from localStorage
        await new Promise((resolve) => setTimeout(resolve, 100));

        console.log('About to fetch problem run');
        // Fetch the problem run
        const problemRun = await fetchProblemRunById(runId);
        console.log('Problem run fetched:', problemRun);

        if (!problemRun || !problemRun.problem) {
          throw new Error("Problem run not found or missing problem data");
        }

        // If in dispute mode with a dispute ID, fetch the specific dispute
        let disputeInfo = null;
        let disputeData = null;
        let allDisputes: any[] = [];
        if (urlIsDisputeMode && disputeId) {
          console.log('Fetching dispute data for dispute ID:', disputeId);
          try {
            const { fetchProblemRunDisputes } = await import("./api");
            const disputes = await fetchProblemRunDisputes(runId);
            console.log('Disputes fetched:', disputes);
            allDisputes = disputes;

            // Find the specific dispute by ID
            const dispute = disputes.find((d) => d.id === disputeId);
            if (!dispute) {
              console.warn("Dispute not found in list, continuing without dispute data");
            } else {
              disputeData = dispute;
              disputeInfo = {
                id: dispute.id,
                status: dispute.status,
                proposedScore: dispute.proposed_score,
                note: dispute.note,
                reason: dispute.reason,
                createdAt: dispute.created_at,
                user_id: dispute.user_id,
                final_score: problemRun.final_score,
                current_score: problemRun.score,
              };
            }
          } catch (disputeError) {
            console.error('Error fetching dispute data:', disputeError);
            // Continue without dispute data rather than failing completely
          }
        } else if (urlIsDisputeMode) {
          // Dispute mode without specific ID - fetch all disputes for the run
          try {
            const { fetchProblemRunDisputes } = await import("./api");
            const disputes = await fetchProblemRunDisputes(runId);
            console.log('Disputes fetched (no specific ID):', disputes);
            allDisputes = disputes;

            // Use the first dispute as the default
            if (disputes.length > 0) {
              const dispute = disputes[0];
              disputeData = dispute;
              disputeInfo = {
                id: dispute.id,
                status: dispute.status,
                proposedScore: dispute.proposed_score,
                note: dispute.note,
                reason: dispute.reason,
                createdAt: dispute.created_at,
                user_id: dispute.user_id,
                final_score: problemRun.final_score,
                current_score: problemRun.score,
              };
            }
          } catch (disputeError) {
            console.error('Error fetching dispute data:', disputeError);
          }
        }

        // Create a run object with the necessary data
        const run: ProblemRunWithProblem & { disputeInfo?: any; allDisputes?: any[] } = {
          ...problemRun,
          disputeInfo,
          allDisputes,
        };

        console.log('Setting selectedRun:', run);
        setSelectedRun(run);
        setProjectRuns([run]); // Only this run

        // No project context when viewing runs directly
        setSelectedProject(null);
        setSelectedProjectData(null);
        setProjectIsReadonly(false);
        setTotalProjectRuns(1);

        // Set viewing mode and dispute data
        if (urlIsDisputeMode) {
          console.log('Setting viewingMistake to true for dispute mode');
          setViewingMistake(true); // Reuse mistake viewing mode for disputes
          if (disputeData) {
            // Set the dispute data for the modal
            setSelectedDispute({
              ...disputeData,
              problem_name: problemRun.problem?.name || "Unknown Problem",
            });
          }
        }
        
        console.log('About to set loading to false in success case');
        setLoading(false);
        setLoadingFromUrl(false);
        setInitialUrlRun(null); // Clear initial URL run after successful load
      } catch (error: any) {
        console.error("Error loading run from URL:", error);

        // Check if it's an auth issue and retry
        const isAuthError =
          error?.response?.status === 401 || error?.response?.status === 403;
        if (isAuthError && !selectedRun) {
          // Don't show error immediately, the auth might still be loading
          console.log(
            "Auth error while loading run, will retry when auth is ready",
          );
          setLoading(false);
          setLoadingFromUrl(false);
          // Keep initialUrlRun so it can retry when auth is ready
          return;
        }

        alert("Error loading problem run. Please try again.");
        // Navigate back to home
        window.history.pushState({}, "", "/");
        // Reset all states
        setSelectedRun(null);
        setProjectRuns([]);
        setSelectedProject(null);
        setSelectedProjectData(null);
        setIsDisputeMode(false);
        setViewingMistake(false);
        setInitialUrlRun(null);
        setLoading(false);
        setLoadingFromUrl(false);
      }
    },
    [isDisputeMode],
  );

  // Handle browser navigation (back/forward buttons and programmatic navigation)
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const runMatch = path.match(/\/run\/([^\/]+)/);

      if (runMatch) {
        const [, runId] = runMatch;
        const params = new URLSearchParams(window.location.search);
        const isDisputeMode = params.get("mode") === "dispute";

        if (isDisputeMode) {
          setIsDisputeMode(true);
        }

        // Load the run
        loadRunFromUrl(runId);
      }
    };

    window.addEventListener("popstate", handlePopState);

    // Also add a custom event listener for programmatic navigation
    const handleCustomNavigation = (event: CustomEvent) => {
      console.log('handleCustomNavigation event received:', event.detail);
      if (event.detail && event.detail.runId && event.detail.isDisputeMode) {
        console.log('Navigating to dispute run:', event.detail.runId);
        setIsDisputeMode(true);
        loadRunFromUrl(event.detail.runId);
      }
    };

    window.addEventListener(
      "navigateToRun",
      handleCustomNavigation as EventListener,
    );

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener(
        "navigateToRun",
        handleCustomNavigation as EventListener,
      );
    };
  }, [loadRunFromUrl]);

  // Handle initial URL run
  useEffect(() => {
    console.log('Initial URL run effect check:', {
      isAuthenticated,
      userId,
      initialUrlRun,
      selectedRun: selectedRun?.id
    });
    
    if (
      isAuthenticated &&
      userId &&
      initialUrlRun &&
      !selectedRun
    ) {
      console.log('Calling loadRunFromUrl for initial URL run:', initialUrlRun);
      loadRunFromUrl(initialUrlRun);
    }
  }, [
    isAuthenticated,
    userId,
    initialUrlRun,
    selectedRun,
    loadRunFromUrl,
  ]);

  const loadProjectAndFirstRun = useCallback(
    async (projectId: string, problemId?: string | null) => {
      setLoading(true);
      try {
        let projectsList = projects;
        if (projects.length === 0) {
          projectsList = await fetchUserProjects();
          setProjects(projectsList);
        }

        const project = projectsList.find((p) => p.id === projectId);
        if (!project) {
          console.error(`Project ${projectId} not found`);
          setSelectedProject(null);
          setLoadingFromUrl(false);
          setLoading(false);
          window.history.pushState({}, "", "/");
          return;
        }

        const { index: unlabeledIndex } = await fetchFirstUnlabeledRun(projectId);
        const runsResponse = await fetchProjectProblemRuns(projectId, 0, 100);

        const tutorialReadonly = Boolean(
          runsResponse.metadata?.tutorial_readonly,
        );

        setProjectIsReadonly(tutorialReadonly);
        setSelectedProjectData({ ...project, tutorial_readonly: tutorialReadonly });

        setProjectRuns(runsResponse.items);
        setTotalProjectRuns(runsResponse.metadata.total || runsResponse.items.length);
        setSelectedProject(projectId);

        // If problemId is provided from URL, use it; otherwise determine it
        let targetProblemId: string | null = null;
        if (problemId) {
          targetProblemId = problemId;
        } else {
          const targetIndex =
            runsResponse.items.length === 0
              ? -1
              : unlabeledIndex !== null && runsResponse.items[unlabeledIndex]
              ? unlabeledIndex
              : 0;

          if (targetIndex >= 0) {
            const targetRun = runsResponse.items[targetIndex];
            targetProblemId = targetRun.problem?.id || targetRun.problem_id || null;
          }
        }

        setSelectedProblemId(targetProblemId);
        setSelectedRun(null);

        // Update URL based on whether we have a problem ID
        if (targetProblemId) {
          window.history.pushState({}, "", `/project/${projectId}/problems/${targetProblemId}`);
        } else {
          window.history.pushState({}, "", `/project/${projectId}`);
        }

        setLoadingFromUrl(false);
        setInitialUrlProject(null);
        setInitialUrlProblem(null);
      } catch (error) {
        console.error("Error loading project:", error);
        setSelectedProject(null);
        setLoadingFromUrl(false);
        setLoading(false);
        setInitialUrlProject(null);
        setInitialUrlProblem(null);
        window.history.pushState({}, "", "/");
      } finally {
        setLoading(false);
      }
    },
    [projects],
  );

  const loadTutorialProject = useCallback(
    async (projectId: string) => {
      setLoading(true);
      try {
        const projectResponse: ProjectResponse = await fetchProject(projectId, {
          tutorialReadonly: true,
        });

        const runsResponse = await fetchProjectProblemRuns(projectId, 0, 100, {
          tutorialReadonly: true,
        });

        let effectiveRuns = runsResponse.items;

        if (!effectiveRuns.length) {
          const config = getTutorialConfig(tutorial.state.software);
          if (config) {
            const fallbackRuns: ProblemRunWithProblem[] = [];
            for (const task of config.tasks) {
              if (!task.runId) continue;
              try {
                const run = await fetchProblemRunById(task.runId);
                const problemId =
                  run.problem?.id || run.problem_id || `tutorial-problem-${task.id}`;
                const problemName =
                  run.problem?.name || task.name || `Tutorial Example (${task.id})`;

                console.debug('[App] Tutorial fallback run fetched', {
                  software: config.software,
                  taskId: task.id,
                  runId: task.runId,
                  problemId,
                  problemName,
                });

                fallbackRuns.push({
                  ...run,
                  problem: {
                    id: problemId,
                    name: problemName,
                    spec: run.problem?.spec || { prompt: run.problem?.spec?.prompt || 'Tutorial example' },
                    is_public: false,
                    software: run.problem?.software || config.software,
                  },
                  problem_id: run.problem_id || problemId,
                });
              } catch (error) {
                console.error(
                  `Failed to preload tutorial run ${task.runId} for ${config.software}:`,
                  error,
                );
              }
            }

            if (fallbackRuns.length) {
              console.debug('[App] Using fallback tutorial runs', {
                software: config.software,
                runCount: fallbackRuns.length,
              });
              effectiveRuns = fallbackRuns;
            }
          }
        }

        const syntheticProject: ProjectWithStatistics = {
          id: projectResponse.id,
          name: projectResponse.name,
          owner_id: projectResponse.owner_id,
          owner_username: projectResponse.owner_username || undefined,
          due_date: projectResponse.due_date || null,
          completed_at: projectResponse.completed_at || null,
          status: projectResponse.status,
          created_at: projectResponse.created_at,
          updated_at: projectResponse.updated_at,
          total_problem_runs: effectiveRuns.length,
          labeled_problem_runs: 0,
          remaining_problem_runs: effectiveRuns.length,
          tutorial_readonly: true,
        };

        setSelectedProject(projectId);
        setSelectedProjectData(syntheticProject);
        setProjectRuns(effectiveRuns);
        setTotalProjectRuns(effectiveRuns.length);
        setProjectIsReadonly(true);
        setTutorialProjectLoadedId(projectId);
        setSelectedRun(null);

        const firstProblemId =
          effectiveRuns[0]?.problem?.id ||
          effectiveRuns[0]?.problem_id ||
          null;

        console.debug('[App] Tutorial project loaded', {
          projectId,
          software: tutorial.state.software,
          apiRunCount: runsResponse.items.length,
          effectiveRunCount: effectiveRuns.length,
          firstProblemId,
        });

        setSelectedProblemId(firstProblemId);

        if (firstProblemId) {
          window.history.pushState(
            {},
            "",
            `/project/${projectId}/problems/${firstProblemId}`,
          );
        } else {
          window.history.pushState({}, "", `/project/${projectId}`);
        }
      } catch (error) {
        console.error("Failed to load tutorial project:", error);
      } finally {
        setLoading(false);
        setLoadingFromUrl(false);
      }
    },
    [],
  );

  // Handle initial URL project (when only project ID is in URL)
  useEffect(() => {
    if (
      isAuthenticated &&
      userId &&
      initialUrlProject &&
      !selectedRun &&
      loadingFromUrl
    ) {
      loadProjectAndFirstRun(initialUrlProject, initialUrlProblem);
    }
  }, [
    isAuthenticated,
    userId,
    hasCompletedOnboarding,
    initialUrlProject,
    initialUrlProblem,
    selectedRun,
    loadingFromUrl,
    loadProjectAndFirstRun,
  ]);

  useEffect(() => {
    if (!tutorial.state.isActive) {
      if (tutorialProjectLoadedId !== null) {
        setTutorialProjectLoadedId(null);
      }
      return;
    }

    const tutorialConfig = getTutorialConfig(tutorial.state.software);
    const tutorialProjectId = tutorialConfig?.projectId;

    if (!tutorialProjectId) {
      return;
    }

    const isCurrentTutorialProject =
      selectedProject === tutorialProjectId && projectIsReadonly;

    if (!isCurrentTutorialProject) {
      if (tutorialProjectLoadedId !== tutorialProjectId) {
        setTutorialProjectLoadedId(null);
        loadTutorialProject(tutorialProjectId);
      }
      return;
    }

    if (tutorialProjectLoadedId !== tutorialProjectId) {
      setTutorialProjectLoadedId(tutorialProjectId);
    }
  }, [
    loadTutorialProject,
    projectIsReadonly,
    selectedProject,
    tutorial.state.isActive,
    tutorial.state.software,
    tutorialProjectLoadedId,
  ]);

  const loadProjectProblemAndRun = async (projectId: string, problemId: string, localRunIndex: number) => {
    setLoading(true);

    try {
      // Load projects if not already loaded
      let projectsList = projects;
      if (projects.length === 0) {
        projectsList = await fetchUserProjects();
        setProjects(projectsList);
      }

      // Find the project data
      const project = projectsList.find((p) => p.id === projectId);
      if (!project) {
        console.error(`Project ${projectId} not found`);
        // Reset state if project not found
        setSelectedProject(null);
        setLoadingFromUrl(false);
        setInitialUrlProject(null);
        setLoading(false);
        window.history.pushState({}, "", "/");
        return;
      }

      // Fetch all runs for the project (paginated)
      const runsPerPage = 100;
      let allRuns: ProblemRunWithProblem[] = [];
      let hasMore = true;
      let page = 0;
      let totalRuns = 0;
      let tutorialReadonly = projectIsReadonly;

      while (hasMore) {
        const { items: runs, metadata } = await fetchProjectProblemRuns(
          projectId,
          page,
          runsPerPage,
        );
        allRuns = [...allRuns, ...runs];
        hasMore = Boolean(metadata?.has_next);

        if (metadata.tutorial_readonly !== undefined) {
          tutorialReadonly = Boolean(metadata.tutorial_readonly);
        }

        // Get total from metadata if available
        if (metadata.total) {
          totalRuns = metadata.total;
        }

        page++;

        // Safety limit to prevent infinite loops
        if (page > 50) {
          console.warn("Aborting project run fetch early to prevent excessive requests");
          break;
        }
      }

      setProjectIsReadonly(Boolean(tutorialReadonly));
      setSelectedProjectData({ ...project, tutorial_readonly: Boolean(tutorialReadonly) });

      // Set the total runs if we got it from metadata
      if (totalRuns > 0) {
        setTotalProjectRuns(totalRuns);
      }

      // Filter runs to only those for the specified problem
      const runsForProblem = allRuns.filter(
        (r) => (r.problem?.id || r.problem_id) === problemId
      );

      if (runsForProblem.length === 0) {
        console.error(`No runs found for problem ${problemId} in project ${projectId}`);
        // Fall back to loading the project normally
        handleProjectSelect(projectId);
        return;
      }

      if (localRunIndex < 0 || localRunIndex >= runsForProblem.length) {
        console.error(`Run index ${localRunIndex} out of bounds for problem ${problemId} (has ${runsForProblem.length} runs)`);
        // Fall back to first run for this problem
        const targetRun = runsForProblem[0];

        setProjectRuns(allRuns);
        setSelectedRun(targetRun);
        setSelectedProblemId(problemId);
        setSelectedProject(projectId);

        // Update URL to correct index
        window.history.pushState(
          {},
          "",
          `/project/${projectId}/problems/${problemId}/runs/0`,
        );
      } else {
        // Get the target run at the local index
        const targetRun = runsForProblem[localRunIndex];

        setProjectRuns(allRuns);
        setSelectedRun(targetRun);
        setSelectedProblemId(problemId);
        setSelectedProject(projectId);

        // URL is already correct, no need to update it
      }
    } catch (error) {
      console.error("Error loading project problem and run:", error);
      // Navigate back to home on error
      window.history.pushState({}, "", "/");
      setSelectedProject(null);
      setSelectedRun(null);
      setProjectRuns([]);
    } finally {
      setLoading(false);
      setLoadingFromUrl(false);
      setInitialUrlProject(null);
    }
  };

  // Tutorial data loading is now handled automatically by TutorialProvider

  const checkOnboardingStatus = async (userId: string) => {
    try {
      // Check if user has completed any tutorial using the actual completion keys from TutorialService
      const hasCompletedFreeCAD =
        localStorage.getItem(`onboarding_v2_completed_${userId}`) === "true";
      const hasCompletedBlender =
        localStorage.getItem(`blender_onboarding_v1_completed_${userId}`) ===
        "true";
      const hasCompletedMuseScore =
        localStorage.getItem(`musescore-tutorial-completed_${userId}`) ===
        "true";

      const hasCompletedModernTutorial = getAllTutorialConfigs().some(
        (config) =>
          localStorage.getItem(`${config.completionKey}_${userId}`) === "true",
      );

      // User has completed onboarding if they've completed ANY tutorial
      // This allows users to access projects even if they haven't done the specific tutorial for their current permissions
      const hasCompleted =
        hasCompletedModernTutorial ||
        hasCompletedFreeCAD ||
        hasCompletedBlender ||
        hasCompletedMuseScore;

      setHasCompletedOnboarding(hasCompleted);
      setLoading(false);
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      setLoading(false);
    }
  };

  const handleOnboardingComplete = async () => {
    // Always mark the Audacity tutorial as completed
    tutorial.completeTutorial();
    setHasCompletedOnboarding(true);

    // Always force the tutorial context to exit
    tutorial.exitTutorial();

    // If we're still showing the tutorial project/run, navigate back to the project list
    const tutorialConfig = getTutorialConfig(tutorial.state.software);
    const isViewingTutorialProject =
      projectIsReadonly ||
      (tutorialConfig?.projectId && selectedProject === tutorialConfig.projectId);

    if (isViewingTutorialProject) {
      setSelectedProject(null);
      setSelectedRun(null);
      setProjectRuns([]);
      setSelectedProblemId(null);
      setSelectedProjectData(null);
      setTutorialProjectLoadedId(null);
    }

    setViewingMistake(false);
    setIsDisputeMode(false);
    setLoadingFromUrl(false);
    setProjectIsReadonly(false);

    if (!projects.length) {
      loadProjects();
    }

    window.location.href = '/';
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      const userProjects = await fetchUserProjects();
      setProjects(userProjects);
      setProjectsLoaded(true);
    } catch (error: any) {
      console.error("Error loading projects:", error);
      console.error("Error details:", error.response || error.message);
      setProjectsLoaded(true); // Even on error, we've attempted to load
    } finally {
      setLoading(false);
    }
  };

  // Tutorial data is now managed by the TutorialService

  // Tutorial data loading is now handled by TutorialService

  // Tutorial data loading is now handled by TutorialService

  const handleProjectSelect = async (projectId: string) => {
    setLoading(true);
    setLoadingFromUrl(false);
    setSelectedProject(projectId);
    setSelectedProblemId(null);

    const tempUrl = `/project/${projectId}/runs/loading`;
    window.history.pushState({}, "", tempUrl);

    try {
      const project = projects.find((p) => p.id === projectId) || null;
      if (project) {
        setTotalProjectRuns(project.total_problem_runs);
      }

      const { index: unlabeledIndex } = await fetchFirstUnlabeledRun(projectId);

      const runsPerPage = 100;
      const targetPage = Math.floor((unlabeledIndex ?? 0) / runsPerPage);
      let allRuns: ProblemRunWithProblem[] = [];
      let hasMore = true;
      let page = 0;
      let tutorialReadonly = projectIsReadonly;

      while (hasMore && (allRuns.length <= (unlabeledIndex ?? 0) || page <= targetPage)) {
        const { items: runs, metadata } = await fetchProjectProblemRuns(
          projectId,
          page,
          runsPerPage,
        );
        allRuns = [...allRuns, ...runs];
        hasMore = Boolean(metadata?.has_next);
        if (metadata.tutorial_readonly !== undefined) {
          tutorialReadonly = Boolean(metadata.tutorial_readonly);
        }
        page++;
      }

      setProjectIsReadonly(Boolean(tutorialReadonly));
      if (project) {
        setSelectedProjectData({ ...project, tutorial_readonly: Boolean(tutorialReadonly) });
      }

      if (allRuns.length === 0) {
        setProjectRuns([]);
        setSelectedRun(null);
        setSelectedProblemId(null);
        window.history.pushState({}, "", `/project/${projectId}`);
        return;
      }

      setProjectRuns(allRuns);

      let targetRunIndex = 0;
      if (unlabeledIndex !== null && allRuns[unlabeledIndex]) {
        targetRunIndex = unlabeledIndex;
      }

      const targetRun = allRuns[targetRunIndex];
      const targetProblemId = targetRun.problem?.id || targetRun.problem_id || null;
      setSelectedRun(null);
      setSelectedProblemId(targetProblemId);

      // Update URL with problem ID if available
      if (targetProblemId) {
        window.history.pushState({}, "", `/project/${projectId}/problems/${targetProblemId}`);
      } else {
        window.history.pushState({}, "", `/project/${projectId}`);
      }
    } catch (error) {
      console.error("Error loading project runs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNextProblem = async () => {
    if (!selectedRun || !projectRuns.length) return;

    // Get the current problem ID
    const currentProblemId = selectedRun.problem?.id || selectedRun.problem_id;

    // Filter runs to only those for the current problem
    const runsForThisProblem = projectRuns.filter(
      (r) => (r.problem?.id || r.problem_id) === currentProblemId
    );

    // Find the index of the current run within runs for this problem
    const indexInProblem = runsForThisProblem.findIndex((r) => r.id === selectedRun.id);

    if (indexInProblem < runsForThisProblem.length - 1) {
      // Navigate to next run for this problem
      const nextRunForProblem = runsForThisProblem[indexInProblem + 1];
      const nextIndexInAllRuns = projectRuns.findIndex((r) => r.id === nextRunForProblem.id);
      selectRunByIndex(nextIndexInAllRuns);
    }
    // If we're at the last run for this problem, button should be disabled (no action)
  };

  const checkProjectCompletion = async () => {
    if (!userId || !projectRuns.length || !selectedProject) return;

    // Check if all runs are labeled by the current user
    let allLabeled = true;
    for (const run of projectRuns) {
      const userHasLabeled =
        run.human_labels &&
        run.human_labels.some((label: any) => label.user_id === userId);
      if (!userHasLabeled) {
        allLabeled = false;
        break;
      }
    }

    if (allLabeled) {
      // Show confirmation dialog
      const confirmed = window.confirm(
        "🎉 Congratulations! You have completed all tasks in this project.\n\n" +
          "⚠️ WARNING: Once you submit this project as complete, you will NOT be able to make any further modifications to your labels.\n\n" +
          "Are you sure you want to submit this project as complete?",
      );

      if (confirmed) {
        try {
          // Call the complete project API
          await completeProject(selectedProject);
          alert("Project successfully submitted as complete!");
          handleBackToProjects();
        } catch (error: any) {
          console.error("Error completing project:", error);
          alert("Failed to complete project. Please try again.");
        }
      }
    } else {
      alert("You have reached the end of this project!");
    }
  };

  const handlePrevProblem = async () => {
    if (!selectedRun || !projectRuns.length) return;

    // Get the current problem ID
    const currentProblemId = selectedRun.problem?.id || selectedRun.problem_id;

    // Filter runs to only those for the current problem
    const runsForThisProblem = projectRuns.filter(
      (r) => (r.problem?.id || r.problem_id) === currentProblemId
    );

    // Find the index of the current run within runs for this problem
    const indexInProblem = runsForThisProblem.findIndex((r) => r.id === selectedRun.id);

    if (indexInProblem > 0) {
      // Navigate to previous run for this problem
      const prevRunForProblem = runsForThisProblem[indexInProblem - 1];
      const prevIndexInAllRuns = projectRuns.findIndex((r) => r.id === prevRunForProblem.id);
      selectRunByIndex(prevIndexInAllRuns);
    }
    // If we're at the first run for this problem, button should be disabled (no action)
  };

  const hasNext = () => {
    if (!selectedRun || !projectRuns.length) return false;

    // Get the current problem ID
    const currentProblemId = selectedRun.problem?.id || selectedRun.problem_id;

    // Filter runs to only those for the current problem
    const runsForThisProblem = projectRuns.filter(
      (r) => (r.problem?.id || r.problem_id) === currentProblemId
    );

    // Find the index of the current run within runs for this problem
    const indexInProblem = runsForThisProblem.findIndex((r) => r.id === selectedRun.id);

    return indexInProblem < runsForThisProblem.length - 1;
  };

  const hasPrev = () => {
    if (!selectedRun || !projectRuns.length) return false;

    // Get the current problem ID
    const currentProblemId = selectedRun.problem?.id || selectedRun.problem_id;

    // Filter runs to only those for the current problem
    const runsForThisProblem = projectRuns.filter(
      (r) => (r.problem?.id || r.problem_id) === currentProblemId
    );

    // Find the index of the current run within runs for this problem
    const indexInProblem = runsForThisProblem.findIndex((r) => r.id === selectedRun.id);

    return indexInProblem > 0;
  };

  const getProgressInfo = () => {
    if (!selectedRun || !projectRuns.length) return { current: 0, total: 0 };

    // Get the current problem ID
    const currentProblemId = selectedRun.problem?.id || selectedRun.problem_id;

    // Filter runs to only those for the current problem
    const runsForThisProblem = projectRuns.filter(
      (r) => (r.problem?.id || r.problem_id) === currentProblemId
    );

    // Find the index of the current run within runs for this problem
    const indexInProblem = runsForThisProblem.findIndex((r) => r.id === selectedRun.id);

    return {
      current: indexInProblem + 1,
      total: runsForThisProblem.length,
    };
  };

  const navigateToFirstRun = async () => {
    if (projectRuns.length > 0) {
      selectRunByIndex(0);
    }
  };


  const handleLogin = async (
    newUserId: string,
    newUsername: string,
    permissions?: string[],
  ) => {
    setUserId(newUserId);
    setUsername(newUsername);
    const perms = permissions || [];
    setUserPermissions(perms);
    setIsAuthenticated(true);
    setLoading(true);

    // Store permissions in localStorage
    localStorage.setItem("userPermissions", JSON.stringify(perms));

    // Notify parent component of auth state change
    onAuthStateChange(newUserId, perms);

    // Identify user in LogRocket
    LogRocket.identify(newUserId, {
      name: newUsername,
      // Add any other user properties you want to track
    });

    // Check onboarding status
    await checkOnboardingStatus(newUserId);
  };

  const handleLogout = () => {
    localStorage.removeItem("userId");
    localStorage.removeItem("username");
    localStorage.removeItem("accessCode");
    localStorage.removeItem("userPermissions");
    setUserId(null);
    setUsername(null);
    setUserPermissions([]);
    setIsAuthenticated(false);
    setSelectedProject(null);
    setSelectedRun(null);
    setProjectIsReadonly(false);
    // Notify parent of logout
    onAuthStateChange(null, []);
  };

  const handleTabChange = (
    tab:
      | "my-projects"
      | "new-projects"
      | "my-mistakes"
      | "my-disputes"
      | "disputes"
      | "resolved-disputes"
      | "conflicting-disputes"
      | "playground",
  ) => {
    setActiveTab(tab);
    const urls = {
      "my-projects": "/",
      "new-projects": "/new-projects",
      "my-mistakes": "/mistakes",
      "my-disputes": "/disputes",
      disputes: "/all-disputes",
      "resolved-disputes": "/resolved-disputes",
      "conflicting-disputes": "/conflicting-disputes",
      playground: "/playground",
    };
    window.history.pushState({}, "", urls[tab]);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setSelectedRun(null);
    setProjectRuns([]);
    setSelectedProblemId(null);
    setSelectedProjectData(null);
    setProjectIsReadonly(false);
    setInitialUrlProject(null);
    setInitialUrlMistake(null);
    setInitialUrlRun(null);

    // If we were viewing a mistake, go back to mistakes tab
    if (viewingMistake && !isDisputeMode) {
      setActiveTab("my-mistakes");
      window.history.pushState({}, "", "/mistakes");
    } else if (isDisputeMode) {
      // Check if user has resolve_disputes permissions
      const hasResolveDisputesPermission = userPermissions.some((perm) =>
        perm.startsWith("resolve_disputes_"),
      );

      if (hasResolveDisputesPermission) {
        // User has permission, go to all-disputes tab
        setActiveTab("disputes");
        window.history.pushState({}, "", "/all-disputes");
      } else {
        // User doesn't have permission, go to my-disputes tab
        setActiveTab("my-disputes");
        window.history.pushState({}, "", "/disputes");
      }
    } else {
      // Otherwise go to projects tab
      setActiveTab("my-projects");
      window.history.pushState({}, "", "/");
    }

    setViewingMistake(false);
    setIsDisputeMode(false);
    setLoadingFromUrl(false); // Reset loading states
    // Invalidate cache and reload projects to get fresh data
    invalidateAPICache("/projects");
    loadProjects();
  };

  useEffect(() => {
    if (!tutorial.state.isActive && projectIsReadonly && selectedProject) {
      handleBackToProjects();
    }
  }, [
    handleBackToProjects,
    projectIsReadonly,
    selectedProject,
    tutorial.state.isActive,
  ]);

  const viewerPlaceholderMessage = projectRuns.length
    ? 'Select a run to begin labeling.'
    : 'No problem runs available.';

  const selectedRunIdValue = selectedRun ? selectedRun.id : null;

  const isTutorialRun =
    tutorial.state.isActive &&
    PROJECT_TUTORIAL_SOFTWARES.has(tutorial.state.software) &&
    projectIsReadonly;

  const isExampleTask = tutorial.state.currentTaskIndex > 0;

  const tutorialRunLoading =
    isTutorialRun &&
    isExampleTask &&
    (
      !tutorialProblem ||
      !tutorialProblem.run ||
      !selectedRun ||
      tutorialProblem.run.id !== selectedRun.id
    );

  useEffect(() => {
    if (
      !tutorial.state.isActive ||
      !PROJECT_TUTORIAL_SOFTWARES.has(tutorial.state.software)
    ) {
      return;
    }

    if (projectRuns.length > 0) {
      return;
    }

    const config = getTutorialConfig(tutorial.state.software);
    if (!config) {
      return;
    }

    const syntheticRuns: ProblemRunWithProblem[] = config.tasks
      .filter((task) => Boolean(task.runId))
      .map((task, index) => {
        const problemId = `tutorial-${task.id}`;
        const fallbackName = task.name || `Tutorial Example ${index + 1}`;
        const promptText = `Tutorial example: ${fallbackName}`;
        const nowIso = new Date(0).toISOString();

        return {
          id: task.runId!,
          problem_id: problemId,
          job_id: `tutorial-job-${task.id}`,
          score: null,
          final_score: null,
          status: 'completed',
          batch_job_name: null,
          created_at: nowIso,
          updated_at: nowIso,
          error: null,
          is_public: false,
          problem: {
            id: problemId,
            name: fallbackName,
            spec: { prompt: promptText },
            is_public: false,
            software: config.software,
          },
        };
      });

    if (syntheticRuns.length) {
      console.debug('[App] Generating synthetic tutorial runs', {
        software: config.software,
        runIds: syntheticRuns.map((run) => run.id),
      });

      setProjectRuns(syntheticRuns);
      setTotalProjectRuns(syntheticRuns.length);

      setSelectedProjectData((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          total_problem_runs: syntheticRuns.length,
          remaining_problem_runs: syntheticRuns.length,
        };
      });

      const firstProblemId =
        syntheticRuns[0]?.problem?.id || syntheticRuns[0]?.problem_id || null;
      if (firstProblemId) {
        setSelectedProblemId((current) => current || firstProblemId);
      }
    }
  }, [
    projectRuns.length,
    tutorial.state.isActive,
    tutorial.state.software,
    setProjectRuns,
  ]);

  useEffect(() => {
    if (!tutorial.state.isActive) {
      return;
    }

    console.debug('[App] Tutorial projectRuns updated', {
      software: tutorial.state.software,
      runCount: projectRuns.length,
      runIds: projectRuns.map((run) => run.id),
      problemIds: projectRuns.map((run) => run.problem?.id || run.problem_id),
    });
  }, [projectRuns, tutorial.state.isActive, tutorial.state.software]);

  // Automatically open example runs once the tutorial advances past the project tour
  useEffect(() => {
    if (
      !tutorial.state.isActive ||
      !PROJECT_TUTORIAL_SOFTWARES.has(tutorial.state.software)
    ) {
      return;
    }

    // Only auto-open viewers for the post-project example tasks
    if (tutorial.state.currentTaskIndex <= 0) {
      return;
    }

    const config = getTutorialConfig(tutorial.state.software);
    const currentStepConfig = config?.steps.find(
      (step) => step.id === tutorial.state.currentStepId,
    );

    if (!currentStepConfig || currentStepConfig.scope !== "viewer") {
      return;
    }

    const tutorialRun = (tutorialProblem?.run || null) as
      | ProblemRunWithProblem
      | null;

    if (!tutorialRun) {
      return;
    }

    const problemData = tutorialRun.problem || tutorialProblem?.problem;
    if (!problemData) {
      return;
    }

    const runWithProblem: ProblemRunWithProblem = tutorialRun.problem
      ? tutorialRun
      : { ...tutorialRun, problem: problemData };

    if (selectedRun && selectedRun.id === runWithProblem.id) {
      return;
    }

    setProjectIsReadonly(true);

    setProjectRuns((prev) => {
      const alreadyPresent = prev.some((run) => run.id === runWithProblem.id);
      if (alreadyPresent) {
        return prev;
      }
      return [...prev, runWithProblem];
    });

    setSelectedRun(runWithProblem);
    setSelectedProblemId(
      runWithProblem.problem?.id || runWithProblem.problem_id || null,
    );

    if (!selectedProject && config?.projectId) {
      setSelectedProject(config.projectId);
    }

    console.debug('[App] Tutorial auto-selected run', {
      software: tutorial.state.software,
      taskIndex: tutorial.state.currentTaskIndex,
      stepId: tutorial.state.currentStepId,
      runId: runWithProblem.id,
      problemId: runWithProblem.problem?.id || runWithProblem.problem_id,
    });
  }, [
    tutorial.state.isActive,
    tutorial.state.software,
    tutorial.state.currentTaskIndex,
    tutorial.state.currentStepId,
    tutorialProblem,
    selectedRun,
    selectedProject,
    setProjectRuns,
  ]);

  if (selectedRun && isTutorialRun) {
    console.debug('[App] Rendering tutorial run viewer', {
      runId: selectedRun.id,
      problemId: selectedRun.problem?.id,
      software: tutorial.state.software,
      projectIsReadonly,
    });
  }

  const viewerSoftware =
    selectedRun && isTutorialRun
      ? tutorial.state.software || 'audacity'
      : selectedRun?.problem?.software || tutorial.state.software;

  const runViewerContent =
    selectedRun && selectedRun.problem ? (
      <ScreenshotViewer
        problemName={selectedRun.problem.name}
        runId={selectedRun.id}
        problemInfo={
          viewingMistake
            ? selectedRun.problem.name
            : `${selectedRun.problem.name} (Run ${getProgressInfo().current} of ${getProgressInfo().total})`
        }
        problemPrompt={
          selectedRun.problem.spec?.prompt || "No prompt available"
        }
        progressInfo={
          viewingMistake ? { current: 1, total: 1 } : getProgressInfo()
        }
        onNextProblem={viewingMistake ? undefined : handleNextProblem}
        onPrevProblem={viewingMistake ? undefined : handlePrevProblem}
        hasNextProblem={viewingMistake ? false : hasNext()}
        hasPrevProblem={viewingMistake ? false : hasPrev()}
        onNavigateToFirst={viewingMistake ? undefined : navigateToFirstRun}
        onBackToProjects={
          viewingMistake || isDisputeMode
            ? handleBackToProjects
            : handleExitRunView
        }
        software={viewerSoftware}
        problemId={selectedRun.problem.id}
        projectId={selectedProject || undefined}
        projectDueDate={
          viewingMistake ? null : selectedProjectData?.due_date || null
        }
        viewingMistake={viewingMistake}
        mistakeInfo={
          (selectedRun as any)?.mistakeInfo ||
          (console.log("No mistakeInfo on selectedRun:", selectedRun),
          undefined)
        }
        disputeInfo={(selectedRun as any).disputeInfo}
        allDisputes={(selectedRun as any).allDisputes}
        isDisputeMode={isDisputeMode}
        onOpenDisputeModal={(dispute) => {
          setSelectedDispute(dispute);
          setShowDisputeResponseModal(true);
        }}
        needsPromptLoad={(selectedRun as any).needsPromptLoad}
        runStatus={selectedRun.status}
        userPermissions={userPermissions}
        onScreenshotsLoaded={handleScreenshotsLoaded}
        onRunLabelChange={handleRunLabelChange}
        isReadOnly={projectIsReadonly}
        isOnboarding={isTutorialRun}
        onOnboardingComplete={handleOnboardingComplete}
        tutorialRunLoading={tutorialRunLoading}
        mattermostState={(selectedRun?.problem?.spec as any)?.start_state?.mattermost_state}
      />
    ) : (
      <div className="viewer-placeholder">{viewerPlaceholderMessage}</div>
    );

  // Helper function to detect software from problem name
  const detectSoftwareFromProblemName = (problemName: string): string => {
    const nameLower = problemName.toLowerCase();
    if (nameLower.includes("blender")) return "blender";
    if (nameLower.includes("freecad")) return "freecad";
    if (nameLower.includes("musescore")) return "musescore";
    if (nameLower.includes("kicad")) return "kicad";
    if (nameLower.includes("odoo")) return "odoo";
    if (nameLower.includes("immich")) return "immich";

    // Default based on user permissions
    if (userPermissions.some((p) => p.includes("blender"))) return "blender";
    if (userPermissions.some((p) => p.includes("musescore")))
      return "musescore";
    if (userPermissions.some((p) => p.includes("kicad"))) return "kicad";

    // Default fallback
    return "freecad";
  };

  const loadMistakeFromUrl = async (runId: string) => {
    setLoading(true);

    try {
      // Fetch the problem run with problem data to get the actual task description
      const problemRun = await fetchProblemRunById(runId);
      const prompt = problemRun.problem?.spec?.prompt || "Task description not available";
      const problemName = problemRun.problem?.name || "Unknown Problem";
      const software = problemRun.problem?.software || detectSoftwareFromProblemName(problemName);

      // We need to also fetch user's incorrect labels to get the mistake info
      const incorrectLabelsResponse = await fetchIncorrectLabels(0, 100);
      const mistake = incorrectLabelsResponse.items.find(
        (m) => m.problem_run_id === runId,
      );

      if (mistake) {
        // Create a run object with the actual prompt and mistake info
        const mistakeRun: ProblemRunWithProblem & { mistakeInfo?: any } = {
          ...problemRun,
          mistakeInfo: {
            userLabel: mistake.user_label,
            actualResult: mistake.final_score === 1.0 ? "success" : "failed",
            labeledAt: mistake.labeled_at,
            humanLabelId: mistake.human_label_id,
            userLabelNotes: mistake.user_label_notes,
            allLabels: mistake.all_labels,
          },
        };

        setSelectedRun(mistakeRun);
        setProjectRuns([mistakeRun]); // Only this run
      } else {
        // If we can't find the mistake info, still show the viewer with the problem run data
        setSelectedRun(problemRun);
        setProjectRuns([problemRun]); // Only this run
      }

      // No project context when viewing mistakes
      setSelectedProject(null);
      setSelectedProjectData(null);
      setTotalProjectRuns(1);
    } catch (error) {
      console.error("Error loading mistake from URL:", error);
      alert("Error loading mistake. Please try again.");
      setViewingMistake(false);
      // Navigate back to home
      window.history.pushState({}, "", "/");
    } finally {
      setLoading(false);
      setLoadingFromUrl(false);
      setInitialUrlMistake(null);
    }
  };

  const handleDisputeSelect = async (dispute: ScoreDispute) => {
    console.log('handleDisputeSelect called with dispute:', dispute);
    
    // Validate dispute object before proceeding
    if (!dispute || !dispute.problem_run_id) {
      console.error('Invalid dispute object - missing problem_run_id:', dispute);
      alert('Error: This dispute is missing required data. Please refresh the page and try again.');
      return;
    }

    // Note: We no longer open the modal directly - users should navigate to the problem run page

    // Otherwise navigate to the mistake view for this problem run
    setViewingMistake(true);
    setLoading(true);
    setLoadingFromUrl(false); // Reset this in case it was set from a previous operation

    try {
      console.log('Fetching messages for problem run:', dispute.problem_run_id);
      console.log('API URL:', `${API_BASE_URL}/problem-runs/${dispute.problem_run_id}/messages`);
      console.log('Auth headers:', getAuthHeaders());
      
      // Check if axios is available
      if (!axios || typeof axios.get !== 'function') {
        throw new Error('Axios is not properly loaded');
      }
      
      // Fetch the messages to extract the prompt
      const response = await axios.get(
        `${API_BASE_URL}/problem-runs/${dispute.problem_run_id}/messages`,
        { headers: getAuthHeaders() },
      );

      const messages = response.data;
      const prompt = extractPromptFromMessages(messages);

      // Detect software from problem name
      const software = detectSoftwareFromProblemName(
        dispute.problem_name || "Unknown Problem",
      );

      // Create a run object with the extracted prompt and dispute info
      const disputeRun: ProblemRunWithProblem & { disputeInfo?: any } = {
        id: dispute.problem_run_id,
        problem_id: "",
        job_id: "",
        status: "completed",
        score: null,
        error: null,
        created_at: "",
        updated_at: "",
        problem: {
          id: "",
          name: dispute.problem_name || "Unknown Problem",
          spec: { prompt } as any,
          is_public: false,
          software: software,
        },
        disputeInfo: {
          status: dispute.status,
          proposedScore: dispute.proposed_score,
          note: dispute.note,
          reason: dispute.reason,
          createdAt: dispute.created_at,
        },
      };

      setSelectedRun(disputeRun);
      // No project context when viewing disputes
      setSelectedProject(null);
      setSelectedProjectData(null);
      setProjectRuns([disputeRun]); // Only this run
      setTotalProjectRuns(1);

      // Update URL to reflect the dispute being viewed
      window.history.pushState(
        {},
        "",
        `/mistake/${dispute.problem_run_id}?mode=dispute&disputeId=${dispute.id}`,
      );
      // Change tab to my-mistakes
      setActiveTab("my-mistakes");
    } catch (error: any) {
      console.error("Error loading dispute:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response,
        stack: error?.stack
      });
      
      // More specific error messages
      if (error?.response?.status === 404) {
        alert("Error: Problem run not found. This dispute may be invalid.");
      } else if (error?.response?.status === 401 || error?.response?.status === 403) {
        alert("Error: You don't have permission to view this dispute.");
      } else if (error?.message?.includes('Network Error')) {
        alert("Error: Network connection issue. Please check your connection and try again.");
      } else {
        alert(`Error loading dispute: ${error?.message || 'Unknown error'}. Please try again.`);
      }
      
      setViewingMistake(false);
      setLoading(false);
      setLoadingFromUrl(false);
    } finally {
      console.log('handleDisputeSelect finally block executed');
      setLoading(false);
    }
  };

  const handleMistakeSelect = async (mistake: any) => {
    console.log("handleMistakeSelect - Full mistake object:", mistake);
    console.log("human_label_id:", mistake.human_label_id);

    // Navigate immediately without waiting for data
    setViewingMistake(true);
    setLoadingFromUrl(false); // Reset this in case it was set from a previous operation

    // Detect software from problem name
    const software = detectSoftwareFromProblemName(mistake.problem_name);

    // Create a minimal run object with just the mistake info
    // The prompt will be loaded in the ScreenshotViewer component
    const mistakeRun: ProblemRunWithProblem & {
      mistakeInfo?: any;
      needsPromptLoad?: boolean;
    } = {
      id: mistake.problem_run_id,
      problem_id: "",
      job_id: "",
      status: "completed",
      score: null,
      error: null,
      created_at: "",
      updated_at: "",
      problem: {
        id: "",
        name: mistake.problem_name,
        spec: { prompt: "Loading..." } as any,
        is_public: false,
        software: software,
      },
      mistakeInfo: {
        userLabel: mistake.user_label,
        actualResult: mistake.final_score === 1.0 ? "success" : "failed",
        labeledAt: mistake.labeled_at,
        humanLabelId: mistake.human_label_id,
        userLabelNotes: mistake.user_label_notes,
        allLabels: mistake.all_labels,
      },
      needsPromptLoad: true, // Flag to indicate prompt needs to be loaded
    };

    console.log(
      "Setting mistake run with mistakeInfo:",
      mistakeRun.mistakeInfo,
    );
    setSelectedRun(mistakeRun);
    // No project context when viewing mistakes
    setSelectedProject(null);
    setSelectedProjectData(null);
    setProjectRuns([mistakeRun]); // Only this run
    setTotalProjectRuns(1);

    // Update URL to reflect the mistake being viewed
    window.history.pushState({}, "", `/mistake/${mistake.problem_run_id}`);
  };

  const handleNextOnboardingTask = async () => {
    tutorial.nextTask();
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Show onboarding if explicitly requested or if new user
  if (showOnboarding && userId && !usesProjectTutorialLayout) {
    return (
      <div className="App">
        <header className="App-header">
          <h1 onClick={handleBackToProjects} style={{ cursor: "pointer" }}>
            {(() => {
              const tutorialConfig = getTutorialForUser(userPermissions);
              if (!tutorialConfig) return "No Tutorial Available";
              const software = tutorialConfig.software;
              return software.charAt(0).toUpperCase() + software.slice(1);
            })()}{" "}
            Evaluation - Tutorial
          </h1>
          <div className="user-info">
            <span>Logged in as: {username}</span>
            <button onClick={handleOnboardingComplete} className="logout-btn">
              Exit Tutorial
            </button>
          </div>
        </header>
        <main className="App-main">
          <ScreenshotViewer
            key={`${tutorial.state.software || 'tutorial'}-task-${currentOnboardingTask}`}
            problemName={
              tutorialProblem?.run?.problem?.name ||
              tutorialProblem?.name ||
              tutorialProblem?.problem?.name ||
              'Loading...'
            }
            runId={tutorialProblem?.run?.id || tutorialProblem?.runId || `tutorial-run-${currentOnboardingTask}`}
            problemInfo="Onboarding Tutorial"
            problemPrompt={
              tutorialProblem?.run?.problem?.spec?.prompt ||
              tutorialProblem?.problem?.spec?.prompt ||
              'Loading tutorial...'
            }
            progressInfo={tutorial.getProgress()}
            onNextProblem={handleNextOnboardingTask}
            onPrevProblem={() => {}}
            hasNextProblem={!tutorial.isLastTask()}
            hasPrevProblem={false}
            isOnboarding={true}
            onOnboardingComplete={handleOnboardingComplete}
            onNavigateToFirst={() => {}}
            currentOnboardingTask={currentOnboardingTask}
            onboardingMessages={tutorialProblem?.messages || []}
            software={tutorial.state.software}
            userPermissions={userPermissions}
          />
        </main>
      </div>
    );
  }

  // Show project list if no project selected (and not loading from URL) and not viewing a mistake
  console.log('Render decision state:', {
    selectedProject,
    initialUrlProject,
    initialUrlMistake,
    initialUrlRun,
    selectedRun,
    loading,
    loadingFromUrl,
    viewingMistake,
    isDisputeMode
  });
  
  if (
    !selectedProject &&
    !initialUrlProject &&
    !initialUrlMistake &&
    !initialUrlRun &&
    !selectedRun
  ) {
    return (
      <div className="App">
        <header className="App-header">
          <h1 style={{ cursor: "pointer" }}>
            {(() => {
              const tutorialConfig = getTutorialForUser(userPermissions);
              if (!tutorialConfig) return "No Tutorial Available";
              const software = tutorialConfig.software;
              return software.charAt(0).toUpperCase() + software.slice(1);
            })()}{" "}
            Evaluation
          </h1>
          <div className="user-info">
            <button onClick={handleTutorialButtonClick} className="tutorial-btn">
              {tutorialButtonLabel}
            </button>
            <span>Logged in as: {username}</span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </header>
        <main className="App-main">
          <ProjectList
            projects={projects}
            onSelectProject={handleProjectSelect}
            onSelectMistake={handleMistakeSelect}
            onSelectDispute={handleDisputeSelect}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            userPermissions={userPermissions}
            loading={loading}
            projectsLoaded={projectsLoaded}
          />
        </main>
        <DisputeResponseModal
          isOpen={showDisputeResponseModal}
          onClose={() => {
            setShowDisputeResponseModal(false);
            setSelectedDispute(null);
          }}
          dispute={selectedDispute}
          onSuccess={() => {
            // Refresh the disputes list after successful response
            window.location.reload();
          }}
        />
      </div>
    );
  }

  // Show problem viewer for selected project
  return (
    <div className="App">
      <header className="App-header">
        <h1 onClick={handleBackToProjects} style={{ cursor: "pointer" }}>
          Software Evaluation
        </h1>
        <div className="user-info">
          {selectedRun && !viewingMistake && !isDisputeMode && (
            <button onClick={handleExitRunView} className="back-btn">
              Exit Run View
            </button>
          )}
          <button onClick={handleBackToProjects} className="back-btn">
            Back to Projects
          </button>
          <button onClick={handleTutorialButtonClick} className="tutorial-btn">
            {tutorialButtonLabel}
          </button>
          <span>Logged in as: {username}</span>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>
      <main className="App-main">
        {loading || loadingFromUrl ? (
          <div className="loading">Loading problem runs...</div>
        ) : selectedProject && !viewingMistake && !isDisputeMode ? (
          selectedRun ? (
            runViewerContent
          ) : (
            <ProjectProblemWorkspace
              project={selectedProjectData}
              projectId={selectedProject}
              runs={projectRuns}
              selectedProblemId={selectedProblemId}
              selectedRunId={selectedRunIdValue}
              onSelectProblem={handleProblemSelect}
              onSelectRun={handleRunSelect}
              onSolutionUploaded={handleSolutionUploaded}
              userId={userId}
              onRunLabelChange={handleRunLabelChange}
              onBadTaskReasonUpdate={handleBadTaskReasonUpdate}
              isReadOnly={projectIsReadonly}
            />
          )
        ) : (
          runViewerContent
        )}
      </main>
      <DisputeResponseModal
        isOpen={showDisputeResponseModal}
        onClose={() => {
          setShowDisputeResponseModal(false);
          setSelectedDispute(null);
        }}
        dispute={selectedDispute}
        onSuccess={() => {
          // Navigate back to all disputes after successful response
          window.history.pushState({}, "", "/all-disputes");
          window.location.reload();
        }}
      />
    </div>
  );
}

// Main App component that provides tutorial context
function App() {
  // Lift authentication state to this level so TutorialProvider gets live updates
  const [userId, setUserId] = useState<string | null>(() => {
    const id = localStorage.getItem("userId");
    return id;
  });

  const [userPermissions, setUserPermissions] = useState<string[]>(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    const perms = storedPermissions ? JSON.parse(storedPermissions) : [];
    return perms;
  });

  // Create a callback to update auth state from AppContent
  const updateAuthState = (
    newUserId: string | null,
    newPermissions: string[],
  ) => {
    setUserId(newUserId);
    setUserPermissions(newPermissions);
  };

  return (
    <TutorialProvider userId={userId} userPermissions={userPermissions}>
      <AppContent onAuthStateChange={updateAuthState} />
    </TutorialProvider>
  );
}

export default App;
